/** @file Creates a rigid mesh attachment (default: the image quad); skin.setEntry maps it to a slot. */
import { z } from 'zod';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { defineCommand } from '../defineCommand';
import { defineCreate } from '../_shared/defineCreate';
import { createShape } from './createShape';
import { meshTopology, type MeshTopology } from './meshTopology';

const MESH_DEFAULTS: MeshTopology & { color: string } = { color: '#ffffffff', uvs: [], triangles: [], hull: 0, vertices: [] };
const index = z.number().int().nonnegative();

const meshCreate = defineCreate({
  domain: 'attachment', kind: 'attachment',
  description: 'Create a rigid mesh attachment on `image` (name or id). Without uvs/triangles/hull it is the image quad (4 vertices, 2 triangles); otherwise uvs (0..1 of the image, y down, 2 per vertex), triangles (index triples), hull (first outline vertices), optional vertices (slot bone space, y up; default: the image centred on the bone) and edges. Not bound to a slot (map it with skin.setEntry); mesh.* commands edit it further.',
  schema: z
    .object({
      ...createShape,
      uvs: z.array(prim.unit).min(6).exactOptional(),
      triangles: z.array(index).min(3).exactOptional(),
      hull: z.number().int().min(3).exactOptional(),
      vertices: prim.numbers.exactOptional(),
      edges: z.array(index).exactOptional(),
    })
    .strict(),
  build(input, ctx) {
    const m = { ...MESH_DEFAULTS, ...input };
    const edges = m.edges === undefined ? {} : { edges: m.edges };
    return { type: 'mesh', name: m.name, image: ctx.resolve('image', m.image), color: m.color, uvs: m.uvs, triangles: m.triangles, hull: m.hull, ...edges, geometry: { kind: 'rigid', vertices: m.vertices } };
  },
});

/** attachment.createMesh: topology is defaulted or checked against the image size before the factory inserts the record. */
export const command = defineCommand({
  ...meshCreate,
  name: 'attachment.createMesh',
  apply(draft, input, ctx) {
    const image = draft.images[ctx.resolve('image', input.image)];
    const topology = meshTopology(input, { w: image?.w ?? 1, h: image?.h ?? 1 });
    meshCreate.apply(draft, { ...input, ...topology }, ctx);
  },
});
